/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import {
  GatewaySessionsResult,
  GatewaySessionsResult$inboundSchema,
} from "../components/gatewaysessionsresult.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ListGatewaySessionsSecurity = {
  sessionHeaderGramSession?: string | undefined;
  apikeyHeaderGramKey?: string | undefined;
};

/**
 * Inbound authorization status.
 */
export const Status = {
  Active: "active",
  Expired: "expired",
  Revoked: "revoked",
  All: "all",
} as const;
/**
 * Inbound authorization status.
 */
export type Status = ClosedEnum<typeof Status>;

/**
 * Ordering; expiry uses the inbound authorization deadline, not access-token expiry. Unknown last-use times sort last.
 */
export const Sort = {
  Newest: "newest",
  Oldest: "oldest",
  LastUsed: "last_used",
  Expiry: "expiry",
} as const;
/**
 * Ordering; expiry uses the inbound authorization deadline, not access-token expiry. Unknown last-use times sort last.
 */
export type Sort = ClosedEnum<typeof Sort>;

export type ListGatewaySessionsRequest = {
  /**
   * Project gateway id. Omit for the Organization Gateway; organization access is then required and the selected project is ignored.
   */
  gatewayId?: string | undefined;
  /**
   * Case-insensitive literal search by subject URN, name, or email.
   */
  search?: string | undefined;
  /**
   * Filter by an upstream account associated with this member server.
   */
  serverId?: string | undefined;
  /**
   * Filter by upstream provider issuer.
   */
  providerId?: string | undefined;
  /**
   * Inbound authorization status.
   */
  status?: Status | undefined;
  /**
   * Inclusive creation time lower bound.
   */
  createdAfter?: Date | undefined;
  /**
   * Exclusive creation time upper bound.
   */
  createdBefore?: Date | undefined;
  /**
   * Ordering; expiry uses the inbound authorization deadline, not access-token expiry. Unknown last-use times sort last.
   */
  sort?: Sort | undefined;
  /**
   * Opaque cursor bound to the current filters and ordering.
   */
  cursor?: string | undefined;
  /**
   * Page size.
   */
  limit?: number | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
  /**
   * API Key header
   */
  gramKey?: string | undefined;
};

export type ListGatewaySessionsResponse = {
  result: GatewaySessionsResult;
};

/** @internal */
export type ListGatewaySessionsSecurity$Outbound = {
  "session_header_Gram-Session"?: string | undefined;
  "apikey_header_Gram-Key"?: string | undefined;
};

/** @internal */
export const ListGatewaySessionsSecurity$outboundSchema: z.ZodMiniType<
  ListGatewaySessionsSecurity$Outbound,
  ListGatewaySessionsSecurity
> = z.pipe(
  z.object({
    sessionHeaderGramSession: z.optional(z.string()),
    apikeyHeaderGramKey: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      sessionHeaderGramSession: "session_header_Gram-Session",
      apikeyHeaderGramKey: "apikey_header_Gram-Key",
    });
  }),
);

export function listGatewaySessionsSecurityToJSON(
  listGatewaySessionsSecurity: ListGatewaySessionsSecurity,
): string {
  return JSON.stringify(
    ListGatewaySessionsSecurity$outboundSchema.parse(
      listGatewaySessionsSecurity,
    ),
  );
}

/** @internal */
export const Status$outboundSchema: z.ZodMiniEnum<typeof Status> = z.enum(
  Status,
);

/** @internal */
export const Sort$outboundSchema: z.ZodMiniEnum<typeof Sort> = z.enum(Sort);

/** @internal */
export type ListGatewaySessionsRequest$Outbound = {
  gateway_id?: string | undefined;
  search?: string | undefined;
  server_id?: string | undefined;
  provider_id?: string | undefined;
  status: string;
  created_after?: string | undefined;
  created_before?: string | undefined;
  sort: string;
  cursor?: string | undefined;
  limit: number;
  "Gram-Session"?: string | undefined;
  "Gram-Key"?: string | undefined;
};

/** @internal */
export const ListGatewaySessionsRequest$outboundSchema: z.ZodMiniType<
  ListGatewaySessionsRequest$Outbound,
  ListGatewaySessionsRequest
> = z.pipe(
  z.object({
    gatewayId: z.optional(z.string()),
    search: z.optional(z.string()),
    serverId: z.optional(z.string()),
    providerId: z.optional(z.string()),
    status: z._default(Status$outboundSchema, "active"),
    createdAfter: z.optional(
      z.pipe(z.date(), z.transform(v => v.toISOString())),
    ),
    createdBefore: z.optional(
      z.pipe(z.date(), z.transform(v => v.toISOString())),
    ),
    sort: z._default(Sort$outboundSchema, "newest"),
    cursor: z.optional(z.string()),
    limit: z._default(z.int(), 50),
    gramSession: z.optional(z.string()),
    gramKey: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      gatewayId: "gateway_id",
      serverId: "server_id",
      providerId: "provider_id",
      createdAfter: "created_after",
      createdBefore: "created_before",
      gramSession: "Gram-Session",
      gramKey: "Gram-Key",
    });
  }),
);

export function listGatewaySessionsRequestToJSON(
  listGatewaySessionsRequest: ListGatewaySessionsRequest,
): string {
  return JSON.stringify(
    ListGatewaySessionsRequest$outboundSchema.parse(listGatewaySessionsRequest),
  );
}

/** @internal */
export const ListGatewaySessionsResponse$inboundSchema: z.ZodMiniType<
  ListGatewaySessionsResponse,
  unknown
> = z.pipe(
  z.object({
    Result: GatewaySessionsResult$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "Result": "result",
    });
  }),
);

export function listGatewaySessionsResponseFromJSON(
  jsonString: string,
): SafeParseResult<ListGatewaySessionsResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListGatewaySessionsResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListGatewaySessionsResponse' from JSON`,
  );
}
